import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { useMapFullscreen } from "../../context/MapFullscreenContext";
import SabiasQue from "../SabiasQue";

// Template131: Día 1 - Bukchon Hanok Village (con mapa)
// Adaptado del componente PageBukchon.jsx de guia-seul para usar las props del sistema guia
const Template131 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [loaded, setLoaded] = useState(true);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(true);
	const { setMapFullscreen } = useMapFullscreen() || {};

	useEffect(() => {
		if (typeof setMapFullscreen === "function") {
			setMapFullscreen(fullscreen);
			return () => setMapFullscreen(false);
		}
	}, [fullscreen, setMapFullscreen]);

	useEffect(() => {
		setIsOnline(navigator.onLine);
		const handleOnline = () => setIsOnline(true);
		const handleOffline = () => setIsOnline(false);
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	const headerText = page?.texts?.[0]?.content;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const bukchonImage = getImageSrc("bukchon");
	const temploImage = getImageSrc("templo");
	const bukchonSticker = getCanvasImageUrl("bukchon-sticker");
	const logoImage = getCanvasImageUrl("logo");

	const mapUrl = page?.maps?.[0] || "";
	const introText = page?.texts?.[1]?.content;
	const bukchonTitle = page?.texts?.[2]?.content;
	const bukchonDesc = page?.texts?.[3]?.content;
	const insadongTitle = page?.texts?.[4]?.content;
	const insadongContent = page?.texts?.[5]?.content;
	const jogyesaTitle = page?.texts?.[6]?.content;
	const jogyesaDesc = page?.texts?.[7]?.content;
	const tipBoxText = page?.texts?.[8]?.content;
	const tipBukchon1 = page?.texts?.[9]?.content;
	const tipBukchon2 = page?.texts?.[10]?.content;
	const tipJogyesa1 = page?.texts?.[11]?.content;
	const tipJogyesa2 = page?.texts?.[12]?.content;

	let actualHeaderColor = headerBackgroundColor || "#2E8B80";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: "#444",
		margin: 0,
	};

	if (fullscreen) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: "#2E8B80",
					}}
				>
					<button
						onClick={() => setFullscreen(false)}
						style={{
							backgroundColor: "#fff",
							color: "#2E8B80",
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: "'Poppins', sans-serif",
						}}
					>
						← Volver a la guía
					</button>
				</Box>
				<iframe
					title="Mapa grande"
					src={mapUrl || "about:blank"}
					width="100%"
					height="100%"
					style={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				></iframe>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						fontSize: "14px",
						fontWeight: 800,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
					}}
				>
					{headerText}
					{logoImage && !isSmallScreen && (
						<Box
							component="img"
							src={logoImage}
							alt="logo"
							sx={{
								width: 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{/* MAPA */}
			<Box data-block-id="map" sx={{ flex: 1, marginTop: "-70px", position: "relative" }}>
				{isOnline && mapUrl ? (
					<iframe
						title="Mapa Bukchon"
						src={mapUrl}
						width="100%"
						height={isSmallScreen ? "400px" : "338px"}
						allowFullScreen=""
						loading="lazy"
						style={{ border: 0 }}
						onLoad={() => setLoaded(true)}
					></iframe>
				) : (
					<Box
						sx={{
							width: "100%",
							height: isSmallScreen ? "400px" : "338px",
							backgroundColor: "#e0e0e0",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						Mapa no disponible offline
					</Box>
				)}

				{isOnline && (
					<button
						onClick={() => setFullscreen(true)}
						style={{
							position: "absolute",
							top: 80,
							right: 10,
							backgroundColor: "#2E8B80",
							color: "#fff",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							textDecoration: "none",
							fontFamily: "'Poppins', sans-serif",
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",

							border: "none",
							cursor: "pointer",
						}}
					>
						Ver mapa grande
					</button>
				)}

				{tipBoxText && (
					<Box
						sx={{
							position: "absolute",
							bottom: 80,
							right: 5,
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 0px 16px",
							padding: "10px 12px",
							fontSize: 11,
							fontFamily: "'Poppins', sans-serif",
							color: "#444",
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: 120,
							zIndex: 3,
						}}
					>
						{tipBoxText}
					</Box>
				)}

				{bukchonSticker && (
					<img
						src={bukchonSticker}
						alt="Sticker"
						style={{
							width: 110,
							position: "absolute",
							bottom: 0,
							right: -10,
							zIndex: 2,
						}}
					/>
				)}
			</Box>

			<Box sx={{ padding: "10px 12px", position: "relative", paddingTop: 0 }}>
				<Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
					{introText && (
						<Box
							data-block-id="text-1"
							component="p"
							sx={{
								fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
								fontSize: 13,
								color: "#333",
								margin: 0,
								padding: 0,
							}}
						>
							{introText}
						</Box>
					)}

					{(bukchonTitle || bukchonDesc || bukchonImage) && (
						<Box data-block-id="text-2" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							{bukchonImage && (
								<img
									src={bukchonImage}
									alt="Bukchon"
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: "12px",
									}}
								/>
							)}
							<Box sx={{ width: "60%" }}>
								{bukchonTitle && (
									<Box component="h3" sx={estiloH3}>
										{bukchonTitle}
									</Box>
								)}
								{bukchonDesc && (
									<Box component="p" sx={estiloP}>
										{bukchonDesc}
									</Box>
								)}
							</Box>
							{tipBukchon1 && (
								<Box
									sx={{
										position: "absolute",
										top: isSmallScreen ? 26 : 6,
										left: "33%",
									}}
								>
									<SabiasQue icono="🏠" texto={tipBukchon1} />
								</Box>
							)}
							{tipBukchon2 && (
								<Box
									sx={{
										position: "absolute",
										top: isSmallScreen ? 66 : 46,
										left: "33%",
									}}
								>
									<SabiasQue icono="🤫" texto={tipBukchon2} />
								</Box>
							)}
						</Box>
					)}

					{(insadongTitle || insadongContent) && (
						<Box
							data-block-id="text-3"
							sx={{
								display: "flex",
								gap: "16px",
								position: "relative",
								marginTop: !isSmallScreen ? "-10px" : 0,
							}}
						>
							<Box sx={{ width: "100%" }}>
								<Box
									sx={{
										...estiloP,
										padding: "12px",
										backgroundColor: "#caeae6",
										borderRadius: "12px",
									}}
								>
									{insadongTitle && (
										<Box component="h3" sx={estiloH3}>
											{insadongTitle}
										</Box>
									)}
									{insadongContent && (
										<Box
											component="span"
											dangerouslySetInnerHTML={{ __html: insadongContent }}
										/>
									)}
								</Box>
							</Box>
						</Box>
					)}

					{(jogyesaTitle || jogyesaDesc || temploImage) && (
						<Box data-block-id="text-4" sx={{ display: "flex", gap: "16px", position: "relative" }}>
							{temploImage && (
								<img
									src={temploImage}
									alt="Templo Jogyesa"
									style={{
										width: "40%",
										height: 110,
										objectFit: "cover",
										borderRadius: "12px",
									}}
								/>
							)}
							{tipJogyesa1 && (
								<Box sx={{ position: "absolute", top: 30, left: "33%" }}>
									<SabiasQue icono="🌲" texto={tipJogyesa1} />
								</Box>
							)}
							{tipJogyesa2 && (
								<Box sx={{ position: "absolute", top: -10, left: "33%" }}>
									<SabiasQue icono="🙏" texto={tipJogyesa2} />
								</Box>
							)}
							<Box sx={{ width: "60%" }}>
								{jogyesaTitle && (
									<Box component="h3" sx={estiloH3}>
										{jogyesaTitle}
									</Box>
								)}
								{jogyesaDesc && (
									<Box component="p" sx={estiloP}>
										{jogyesaDesc}
									</Box>
								)}
							</Box>
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};

Template131.thumbnail = "/images/template-1.png";

export default Template131;
